import { Info } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import CategoryPicker from "@/components/CategoryPicker";
import MeasurementsBlock from "@/components/MeasurementsBlock";
import SizePicker from "@/components/SizePicker";
import { formatMoney, currencySymbol } from "@/lib/currency";

/**
 * SpecsSection — product spec block (title, sku, category, measurements, size, weight, …).
 * Pure render component; all state lives on the parent.
 */
export function SpecsSection({ item, categories, unitSystem, fld, update, setItem, loadCategories, weightField }) {
  return (
    <section className="bg-linen border border-taupe/50 p-8 mb-10">
      <h2 className="font-serif text-2xl lowercase text-charcoal mb-6">product specs</h2>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
        {fld("title", "title")}
        {fld("sku", "sku")}
        {fld("brand", "brand")}
        {fld("collection", "collection")}
        {fld("fabric", "fabric / material")}
        {fld("color", "color")}
        <CategoryPicker
          value={item.category || ""}
          onChange={(slug) => update("category", slug)}
          onManagerClose={loadCategories}
        />
        {fld("type", "type")}
        {fld("style", "style")}
        {fld("storage_location", "storage location · where the piece lives")}
        {fld("source_vendor", "source / vendor · where you sourced it")}
        {fld("purchase_date", "purchase date", "date")}
        <div className="md:col-span-2">
          <label className="editorial-label">description</label>
          <textarea
            value={item.description || ""}
            onChange={(e) => update("description", e.target.value)}
            rows={4}
            data-testid="field-description"
            className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal"
          />
        </div>
        <MeasurementsBlock
          schema={categories.find((c) => c.slug === item.category)?.measurement_schema || []}
          values={item.measurements_map || {}}
          unitSystem={unitSystem}
          onChange={(next) => update("measurements_map", next)}
          fallbackValue={item.measurements || ""}
          onFallbackChange={(v) => update("measurements", v)}
        />
        <SizePicker
          fit={item.size_fit || ""}
          system={item.size_system || ""}
          value={item.size_value || ""}
          onChange={({ fit, system, value, label }) => {
            setItem({ ...item, size_fit: fit, size_system: system, size_value: value, size: label });
          }}
        />
        {weightField}
      </div>
    </section>
  );
}

function BreakdownRow({ label, value, sign, bold, accent, rawValue }) {
  const formatted = rawValue ? Number(value).toFixed(3) : formatMoney(Number(value || 0));
  return (
    <div className={`flex justify-between py-2 ${bold ? "font-medium" : ""} ${accent ? "text-brass" : "text-charcoal"}`}>
      <span className="text-sm">{sign && <span className="mr-2 text-taupe">{sign}</span>}{label}</span>
      <span className={`font-mono ${accent ? "text-brass font-serif text-2xl" : "text-sm"}`}>{formatted}</span>
    </div>
  );
}

/**
 * FinancialsSection — purchase + shipping + margin inputs with a live breakdown panel and the
 * calculated sale-price / take-home block at the bottom.
 */
export function FinancialsSection({ item, fld, showBreakdown, setShowBreakdown, computedPrice, update }) {
  const [uploading, setUploading] = useState(false);
  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("source_receipt_path", data.path);
      toast.success("receipt attached.");
    } catch { toast.error("upload failed."); } finally { setUploading(false); }
  };
  return (
    <section className="bg-linen border border-taupe/50 p-8 mb-10" data-testid="financials-section">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="font-serif text-2xl lowercase text-charcoal">financials</h2>
          <p className="text-xs text-espresso mt-1">platform fee · {(item.platform_fee_percentage * 100).toFixed(1)}% (pulled live from institutional settings)</p>
        </div>
        <button
          onClick={() => setShowBreakdown((v) => !v)}
          data-testid="toggle-breakdown"
          className="text-xs tracking-[0.25em] uppercase border border-brass text-brass hover:bg-brass hover:text-ivory px-3 py-2 transition-colors flex items-center gap-2"
        >
          <Info className="w-3 h-3" /> {showBreakdown ? "hide breakdown" : "show breakdown"}
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-6">
        {fld("purchase_cost", "purchase cost", "number", currencySymbol())}
        {fld("shipping_cost", "shipping cost", "number", currencySymbol())}
        {fld("restoration_cost", "cleaning / repair", "number", currencySymbol())}
        {fld("profit_margin", "desired profit (margin)", "number", currencySymbol())}
      </div>
      <p className="text-[0.65rem] text-taupe mt-3 tracking-widest uppercase">cleaning & repair costs are folded into cogs at sale (sched-c reg §1.471-3). margin is added to cost basis and grossed up by the platform fee.</p>

      {/* Source receipt — IRS purchase-evidence */}
      <div className="mt-6 border-t border-taupe/30 pt-6">
        <p className="editorial-label mb-2">source receipt · purchase evidence</p>
        {item.source_receipt_path ? (
          <div className="flex items-center gap-4">
            <a href={fileUrl(item.source_receipt_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs uppercase tracking-widest border border-emerald-700/40 px-3 py-2 text-emerald-800 hover:bg-emerald-50">
              ✓ view attached receipt
            </a>
            <button onClick={() => update("source_receipt_path", null)} className="text-xs text-red-700 underline" data-testid="receipt-remove">remove</button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 border border-taupe/40 px-3 py-2 bg-ivory cursor-pointer text-xs uppercase tracking-widest" data-testid="receipt-upload">
            {uploading ? "uploading…" : "upload purchase receipt"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadReceipt(e.target.files?.[0])} />
          </label>
        )}
        <p className="text-[0.65rem] text-taupe mt-2 tracking-widest uppercase">tip · for multi-item receipts, use studio → inventory → &quot;log sourcing trip&quot; to split one receipt across many pieces.</p>
      </div>

      {showBreakdown && (
        <div className="mt-6 bg-ivory border border-brass/40 p-6" data-testid="price-breakdown">
          <p className="editorial-label mb-4">precise pricing logic · step by step</p>
          <BreakdownRow label="purchase cost" value={item.purchase_cost} sign="+" />
          <BreakdownRow label="shipping cost" value={item.shipping_cost} sign="+" />
          <BreakdownRow label="cleaning / repair" value={item.restoration_cost} sign="+" />
          <BreakdownRow label="profit margin" value={item.profit_margin} sign="+" />
          <BreakdownRow label="subtotal · cost + margin" value={Number(item.purchase_cost || 0) + Number(item.shipping_cost || 0) + Number(item.restoration_cost || 0) + Number(item.profit_margin || 0)} bold />
          <BreakdownRow label={`÷ (1 − ${(item.platform_fee_percentage * 100).toFixed(1)}%)`} value={1 - item.platform_fee_percentage} sign="÷" rawValue />
          <div className="border-t border-taupe/40 mt-3 pt-3">
            <BreakdownRow label="sale price" value={Number(computedPrice)} bold accent />
          </div>
          <p className="text-[0.65rem] text-taupe mt-3 tracking-widest uppercase">formula · (purchase + shipping + restoration + margin) ÷ (1 − platform fee %) = sale price</p>
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-taupe/50 flex items-center justify-between">
        <div>
          <p className="editorial-label">calculated sale price</p>
          <p className="font-serif text-4xl text-brass mt-2" data-testid="computed-price">{formatMoney(computedPrice)}</p>
        </div>
        <div className="text-right">
          <p className="editorial-label">your take-home per piece</p>
          <p className="font-serif text-2xl text-charcoal mt-2" data-testid="net-margin">{formatMoney(item.profit_margin || 0)}</p>
        </div>
      </div>
    </section>
  );
}
